import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { pagedQuery, pagedResponse } from './pagination.js';
import { CurrencySchema, IsoDateSchema, MessageSchema, MoneyStringSchema, } from './primitives.js';
const c = initContract();
/**
 * The Banks and Checks group (PART 11 §11.2): FIN-09 Bank Accounts +
 * reconciliation, FIN-10 Incoming Checks, FIN-11 Outgoing Checks, FIN-12
 * Bank Operations — plus FIN-06 Cash Transfer, which 16.2.2 row 7 places in
 * the `banking` area ("Bank accounts, cheques, reconciliation, cash
 * transfers"; PART 16 wins over FIN-06 §9's accounting-area leftover).
 *
 * Permission mapping, per the §9 tables read through the 12×5 grid:
 *   read anything                 = banking.view
 *   create records / draft docs   = banking.create
 *   edit records, ticks, drafts   = banking.edit
 *   transitions that post ledger  = banking.approve  (deposit/clear/given/
 *     legs, reverse, adjustment,     presented/bounce, Mark as reconciled and
 *     complete/reopen, post          its withdrawal — FIN-09 §9: the same
 *     transfer)                      permission makes and withdraws the
 *                                    assertion)
 *   void a cheque / delete draft  = banking.delete  (FIN-11 §9: voiding
 *                                    destroys a payment instrument — "the
 *                                    narrowest permission on the screen")
 *   edit the bounced-cheque flag  = banking.override_bounced_flag (R10 —
 *                                    the one named extension to the grid,
 *                                    granted to NO shipped role; the Owner
 *                                    grants it deliberately, like payroll.*)
 */
// The bounced-flag override permission itself (OVERRIDE_BOUNCED_FLAG) lives
// in permissions.ts with the rest of the vocabulary.
// ── The fixed sentences of the banking screens ──────────────────────────────
export const BANKING_MESSAGES = {
    // FIN-09 — bank accounts and reconciliation.
    ledgerAccountKind: 'The linked ledger account must be a postable balance-sheet account — a cash-family asset or a liability.',
    ledgerAccountTaken: 'That ledger account is already linked to another bank account.',
    unmappedAccount: 'This account has no linked ledger account. Map it before reconciling.',
    /** ERR-FIN-11-04 — the corporate card is not in the list, and is refused here too. */
    drawnOnKind: 'An outgoing cheque must not be drawn on a petty-cash or credit-card account.',
    depositTargetKind: 'An incoming cheque must not be deposited into a credit-card account.',
    depositTargetRequired: 'Choose the account the cheque is deposited into.',
    /** ERR-FIN-09-03 — the tick boxes go inert once the period is reconciled. */
    reconciliationLocked: 'This period is reconciled and locked. Cleared transactions can no longer be edited.',
    differenceNotZero: 'An account must not be marked reconciled while the difference is not zero.',
    reconciliationExists: 'A reconciliation for this account and statement date already exists.',
    // FIN-10 / FIN-11 — cheques.
    zeroCheque: 'A cheque must be written for an amount above zero.',
    /** ERR-FIN-10-05, including its recovery: cancel and record it again. */
    amountLocked: 'The cheque amount cannot be edited — cancel the cheque and record it correctly.',
    illegalTransition: (from, to) => `A cheque cannot move from ${from} to ${to} — only legal next states are offered.`,
    customerRequired: 'An incoming cheque must name a customer.',
    supplierOrPayeeRequired: 'An outgoing cheque must name a registered supplier or a one-off payee.',
    drawnOnRequired: 'An outgoing cheque must be drawn on one of the company’s own bank accounts.',
    earlyDepositBlocked: 'This cheque is post-dated and early deposit is switched off. It can be deposited on its cheque date.',
    /**
     * D31 — the refusal a HELD cheque gives to its next lifecycle move.
     *
     * DELIBERATELY UNPARAMETERISED, and the date is the thing it is tempting to
     * put in. `server-messages.ts` maps whole sentences to i18n keys BY EXACT
     * MATCH, so a placeholder here would silently un-localise the refusal for
     * every Arabic reader — the sentence would never match its entry again. The
     * date the reader wants is on the cheque in front of them, drawn by
     * `banking.postingHeld` ("Posts on {date}") on the register row and in the
     * detail panel; the refusal only has to say why the button did nothing.
     *
     * The English value of `banking.errPostingHeld` is this sentence byte for
     * byte, asserted in `apps/web/src/test/contract-sentence-render.test.tsx`.
     */
    postingHeldTransition: 'This cheque is post-dated. Its entry is held until the cheque date, so the cheque cannot move on yet.',
    /** ERR-FIN-11-01, with the recovery the spec gives, in the sentence. */
    voidOnlyIssued: 'A cheque can only be voided while it is still Issued. Stop the cheque with the bank, or wait for it to clear or bounce.',
    /** ERR-FIN-11-02 — the half-opacity button, said in words. */
    voidReasonRequired: 'Voiding requires a reason. The button is inert until one is given.',
    /**
     * ERR-FIN-10-07 under A12 (26 Aug 2026) — the reason box the prototype
     * lacked, required now: `voidReasonRequired`'s mirror, for the incoming
     * cheque handed back to its customer. Deliberately unparameterised, like
     * its twin, so the exact-match map can localize it.
     */
    returnReasonRequired: 'Returning a cheque to the customer requires a reason. The button is inert until one is given.',
    voidedNotice: 'Voided — this cheque number is retained in the audit trail as voided and does not affect the supplier balance.',
    reverseOnlyBounced: 'Only a bounced cheque can be reversed.',
    reverseOnce: 'This cheque has already been reversed — reversal is one-shot.',
    reissueOnlyBounced: 'Only a bounced cheque can be reissued.',
    reissueOnce: 'A replacement cheque already exists for this one.',
    /** FIN-10 §7's result note, stated by the reversal response. */
    reversedIncoming: 'Reversed — the invoice allocation was undone (balance restored to unpaid) and a journal entry posted for the reversal plus any bounce fee. Customer flagged for credit risk.',
    /** FIN-11 §6's briefer note — no party is flagged. */
    reversedOutgoing: 'Reversed — the bill allocation was undone (balance restored) and a journal entry posted for the reversal plus any bounce fee.',
    /** BR-FIN-10-16 — verbatim. */
    bouncedFlagDenied: 'You do not have permission to change a customer’s bounced-cheque flag. Ask an administrator to grant it.',
    bouncedFlagReasonRequired: 'A manual change to the bounced-cheque flag requires a reason.',
    /**
     * ERR-MD-01-10, verbatim from MD-01 §14 — the CLEARING case's own sentence.
     * BR-FIN-10-16 requires the reason on every manual change but words no
     * refusal for an empty one; the MD-01 row does, for the untick alone, so the
     * untick answers with it and a hand-RAISE keeps the neutral wording above.
     */
    bouncedFlagClearReasonRequired: 'Say why you are clearing the bounced-cheque flag. The reason goes in the audit log.',
    bouncedFlagCustomerOnly: 'The bounced-cheque flag belongs to customers.',
    // FIN-06 — cash transfers.
    sameAccounts: 'From and To accounts must be different.',
    /** ERR-FIN-06-02 — the greyed Post button's server half. */
    transferAmountRequired: 'Enter an amount above zero before posting.',
    /** ERR-FIN-06-07 — every field read-only once posted. */
    transferPostedLocked: 'This transfer is posted and locked. A journal entry moved the funds and both account balances were updated.',
    transferCancelledLocked: 'This transfer is cancelled and read-only.',
    /**
     * GEN-59 (20260921100000). The SALES shape, not the stock-transfer one,
     * for two reasons that point the same way: this sentence is mapped
     * (`server-messages.ts` → `banking.errCancelReasonRequired`), and D31's
     * gate requires a mapped sentence's English dictionary value to BE the
     * contract sentence — `SALES_MESSAGES.cancelReasonRequired` is the one
     * mapped precedent and is exactly this shape. It is also deliberately not
     * byte-equal to `TRANSFER_MESSAGES.cancelReasonRequired`'s "A transfer can
     * only be…": two constants sharing one sentence would collide on one
     * exact-match map key, and the first wave to add either entry would
     * silently re-route the other's refusal.
     */
    cancelReasonRequired: 'Say why this transfer is being cancelled.',
    transferCashAccountsOnly: 'Transfers move money between the accounts inside Cash & Cash Equivalents.',
    feeExpenseKind: 'The fee expense account must be an expense account.',
    /** ERR-FIN-06-04 — "Record as expense" chosen with the account left empty. */
    feeExpenseRequired: 'Record as expense needs a fee expense account.',
    noRouteOutOfPostedTransfer: 'A posted transfer is corrected by a reversing entry, never rewritten and never cancelled.',
};
/**
 * D46 — the cheque illegal-transition refusal, structured. `from`/`to` are raw
 * CheckStatus TOKENS (one, `in_hand`, carries an underscore); en interpolates
 * them verbatim (the twin is bare), ar selects them into Arabic, so the raw
 * token is absent from the Arabic render. Sentence parity pinned by
 * `refusal-census.spec.ts`.
 */
export const BANKING_REFUSALS = {
    illegalTransition: (from, to) => ({
        message: BANKING_MESSAGES.illegalTransition(from, to),
        key: 'banking.errIllegalTransition',
        params: { from, to },
    }),
};
// ── Bank accounts (FIN-09) ──────────────────────────────────────────────────
export const BankAccountTypeSchema = z.enum(['bank', 'cash', 'petty_cash', 'credit_card']);
export const BankAccountSchema = z
    .object({
    id: z.uuid(),
    name: z.string(),
    nameAr: z.string().nullable(),
    bankName: z.string().nullable(),
    accountNumber: z.string().nullable(),
    branch: z.string().nullable(),
    swiftBic: z.string().nullable(),
    currency: z.string(),
    accountType: BankAccountTypeSchema,
    ledgerAccountId: z.uuid().nullable(),
    ledgerAccountCode: z.string().nullable(),
    ledgerAccountName: z.string().nullable(),
    openingBalance: z.string(),
    openingDate: IsoDateSchema,
    /**
     * BR-FIN-09-03: opening balance + every POSTED movement on the linked
     * ledger account (R9). Null while unmapped — the balance cannot be known.
     */
    currentBalance: z.string().nullable(),
    isActive: z.boolean(),
    /** FIN-09 §7: whether the latest period is completed. */
    reconciliationState: z.enum(['reconciled', 'pending']),
    notes: z.string().nullable(),
    createdAt: z.string(),
})
    .strict();
export const CreateBankAccountRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    nameAr: z.string().trim().max(120).optional(),
    bankName: z.string().trim().max(120).optional(),
    accountNumber: z.string().trim().max(60).optional(),
    branch: z.string().trim().max(120).optional(),
    swiftBic: z.string().trim().max(20).optional(),
    /**
     * FIN-09 field 5 defaults to `USD` — which is the seeded BASE of every
     * tenant, not a constant. The default is resolved in the service from the
     * company's own row (GEN-107), the same way MD-01 field 21 and MD-08 do
     * it, so a tenant whose base is EUR does not silently open EUR bank
     * accounts denominated in dollars. Optional here, never absent in storage.
     */
    currency: CurrencySchema.optional(),
    accountType: BankAccountTypeSchema.default('bank'),
    ledgerAccountId: z.uuid().optional(),
    openingBalance: MoneyStringSchema.optional(),
    openingDate: IsoDateSchema,
    notes: z.string().max(2000).optional(),
})
    .strict();
export const UpdateBankAccountRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(120).optional(),
    nameAr: z.string().trim().max(120).nullable().optional(),
    bankName: z.string().trim().max(120).nullable().optional(),
    accountNumber: z.string().trim().max(60).nullable().optional(),
    branch: z.string().trim().max(120).nullable().optional(),
    swiftBic: z.string().trim().max(20).nullable().optional(),
    currency: CurrencySchema.optional(),
    accountType: BankAccountTypeSchema.optional(),
    ledgerAccountId: z.uuid().nullable().optional(),
    openingBalance: MoneyStringSchema.optional(),
    openingDate: IsoDateSchema.optional(),
    isActive: z.boolean().optional(),
    notes: z.string().max(2000).nullable().optional(),
})
    .strict();
// ── Cheques (FIN-10 / FIN-11) ───────────────────────────────────────────────
export const CheckDirectionSchema = z.enum(['incoming', 'outgoing']);
export const CheckStatusSchema = z.enum([
    'in_hand',
    /** PV-01: an in-hand cheque handed on to a supplier by a payment voucher. Terminal. */
    'endorsed',
    'deposited',
    'cleared',
    'bounced',
    'returned',
    'cancelled',
    'issued',
    'given',
    'presented',
    'voided',
]);
export const BounceReasonSchema = z.enum([
    'insufficient_funds',
    'signature_mismatch',
    'account_closed',
    'other',
]);
export const CheckSchema = z
    .object({
    id: z.uuid(),
    /** ICQ-#### / OCQ-#### (FIN-10/-11 field 1). */
    code: z.string(),
    checkNo: z.number().int().positive(),
    direction: CheckDirectionSchema,
    /** The number printed on the cheque — a separate fact (BR-FIN-10-01). */
    chequeNumber: z.string(),
    partnerId: z.uuid().nullable(),
    partnerName: z.string().nullable(),
    /** 5a: the customer's credit-risk flag, drawn beside the name. */
    partnerBouncedCheque: z.boolean().nullable(),
    payeeName: z.string().nullable(),
    issuingBank: z.string().nullable(),
    bankAccountId: z.uuid().nullable(),
    bankAccountName: z.string().nullable(),
    amount: z.string(),
    currency: z.string(),
    /** Date received (incoming) / issue date (outgoing). */
    docDate: IsoDateSchema,
    chequeDate: IsoDateSchema,
    status: CheckStatusSchema,
    depositDate: IsoDateSchema.nullable(),
    presentedDate: IsoDateSchema.nullable(),
    clearanceDate: IsoDateSchema.nullable(),
    bounceDate: IsoDateSchema.nullable(),
    bounceReason: BounceReasonSchema.nullable(),
    bounceFee: z.string().nullable(),
    voidReason: z.string().nullable(),
    voidedAt: z.string().nullable(),
    /** A12: void_reason's mirror — why an in-hand cheque went back (ERR-FIN-10-07).
     *  NULL on non-returned cheques and on returns that predate the ruling. */
    returnReason: z.string().nullable(),
    receiptId: z.uuid().nullable(),
    paymentId: z.uuid().nullable(),
    reissuedFromCheckId: z.uuid().nullable(),
    /** Derived: the replacement written for this bounced cheque, if any. */
    replacementCheckId: z.uuid().nullable(),
    depositJournalEntryId: z.uuid().nullable(),
    clearanceJournalEntryId: z.uuid().nullable(),
    reversalJournalEntryId: z.uuid().nullable(),
    reversedAt: z.string().nullable(),
    /**
     * D31 — THE PENDING OBLIGATION, on the wire.
     *
     * Item 31's option 1 in one field: "an unposted obligation exists in the
     * meantime, which has to be visible somewhere". Non-null means this
     * cheque's next ledger leg is held and posts on this date; null means
     * nothing is waiting. The register draws it as an amber chip beside the
     * status — amber because NFR-14 pins amber to "waiting for somebody or
     * something", which is exactly what a held posting is.
     */
    postingDueOn: IsoDateSchema.nullable(),
    /**
     * D19's cheque door completed: the rate this cheque's first leg posted
     * with, and BR-SAL-04-09's "as of" date beside it — "the exchange rate
     * must be snapshotted on the invoice date, AND THE 'AS OF' DATE MUST BE
     * SHOWN", because a rate without its date can be repeated but not
     * defended. Both null while nothing has posted, which includes a held
     * post-dated cheque: the screen says "Not frozen until posted" (fx.
     * notFrozen), which is true of a draft document and of a held cheque for
     * the same reason (BR-MD-11-11).
     */
    exchangeRate: z.string().nullable(),
    rateEffectiveOn: IsoDateSchema.nullable(),
    /**
     * checks.alert_out_of_range_number: advisory — the printed number is not
     * numeric or jumps the account's sequence (Q-FIN-11-5's default, scoped:
     * no cheque books yet, so "in range" = within 50 of the account's last).
     */
    numberOutOfRange: z.boolean(),
    notes: z.string().nullable(),
    createdAt: z.string(),
})
    .strict();
export const CreateCheckRequestSchema = z
    .object({
    direction: CheckDirectionSchema,
    chequeNumber: z.string().trim().min(1).max(40),
    partnerId: z.uuid().optional(),
    payeeName: z.string().trim().min(1).max(120).optional(),
    issuingBank: z.string().trim().max(120).optional(),
    /** Outgoing: the drawn-on account, required (BR-FIN-11-01). */
    bankAccountId: z.uuid().optional(),
    amount: MoneyStringSchema,
    currency: CurrencySchema.optional(),
    docDate: IsoDateSchema,
    chequeDate: IsoDateSchema,
    notes: z.string().max(2000).optional(),
})
    .strict();
export const UpdateCheckRequestSchema = z
    .object({
    chequeNumber: z.string().trim().min(1).max(40).optional(),
    issuingBank: z.string().trim().max(120).nullable().optional(),
    docDate: IsoDateSchema.optional(),
    chequeDate: IsoDateSchema.optional(),
    bounceReason: BounceReasonSchema.optional(),
    /** Deliberately clearable (FIN-10 field 16). */
    bounceFee: MoneyStringSchema.nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
})
    .strict();
/** The lifecycle moves a service transition may perform. Terminal states and
 *  void have their own routes (void carries a reason; reversal is not a
 *  status). */
export const CheckTransitionRequestSchema = z
    .object({
    to: z.enum(['deposited', 'cleared', 'bounced', 'returned', 'cancelled', 'given', 'presented']),
    /** The auto-stamp override: a typed date is never overwritten (BR-FIN-10-05). */
    date: IsoDateSchema.optional(),
    /** Deposit target (incoming, required at deposit — BR-FIN-10-07). */
    bankAccountId: z.uuid().optional(),
    bounceReason: BounceReasonSchema.optional(),
    bounceFee: MoneyStringSchema.optional(),
    /**
     * A12 / ERR-FIN-10-07: required when `to` is 'returned' — but non-blank
     * is enforced IN THE SERVICE, not here, the `SetBouncedFlagRequest`
     * shape: only the handler can answer an empty reason with a `{ message }`
     * body carrying `returnReasonRequired`'s sentence; a `.min(1)` here would
     * refuse first in the validation layer's own shape and no sentence would
     * reach anybody.
     */
    returnReason: z.string().trim().max(500).optional(),
})
    .strict();
export const SetBouncedFlagRequestSchema = z
    .object({
    flagged: z.boolean(),
    /**
     * Required on every manual change (BR-FIN-10-16) — but non-blank is
     * enforced IN THE HANDLER, not here, the BR-INV-06-06 shape: BR-FIN-10-16
     * says "the handler must refuse the write however it is called", and only
     * the handler can answer the empty-reason case with a `{ message }` body
     * carrying the right sentence — ERR-MD-01-10's for an untick, the neutral
     * one for a hand-raise. A `.min(1)` here refused first, in the validation
     * layer's own shape, and no sentence ever reached anybody.
     */
    reason: z.string().trim().max(200),
})
    .strict();
export const BouncedFlagHistoryRowSchema = z
    .object({
    occurredAt: z.string(),
    /** 'system' on a reversal, the named user on a manual change (BR-FIN-10-17). */
    actorType: z.string(),
    actorEmail: z.string().nullable(),
    before: z.boolean().nullable(),
    after: z.boolean().nullable(),
    reason: z.string().nullable(),
})
    .strict();
// ── Reconciliation (FIN-09 §5d–§7) ──────────────────────────────────────────
export const ReconciliationLineSchema = z
    .object({
    /** Null on a book row never ticked yet — the tick creates the row. */
    id: z.uuid().nullable(),
    source: z.enum(['book', 'adjustment', 'statement']),
    journalLineId: z.uuid().nullable(),
    lineDate: IsoDateSchema.nullable(),
    description: z.string().nullable(),
    reference: z.string().nullable(),
    /** Signed: + money in, − money out (§5e). */
    amount: z.string(),
    cleared: z.boolean(),
    adjustmentKind: z.enum(['bank_fee', 'interest']).nullable(),
})
    .strict();
export const ReconciliationSchema = z
    .object({
    id: z.uuid(),
    bankAccountId: z.uuid(),
    statementDate: IsoDateSchema,
    statementBalance: z.string(),
    status: z.enum(['in_progress', 'completed']),
    completedAt: z.string().nullable(),
    completedByEmail: z.string().nullable(),
    completedBalance: z.string().nullable(),
    /** The §5d computed figures — R4/R5/R6/R3. */
    systemBalance: z.string(),
    clearedBalance: z.string(),
    outstanding: z.string(),
    difference: z.string(),
    lines: ReconciliationLineSchema.array(),
})
    .strict();
export const ReconciliationHistoryRowSchema = z
    .object({
    id: z.uuid(),
    statementDate: IsoDateSchema,
    statementBalance: z.string(),
    status: z.enum(['in_progress', 'completed']),
    completedAt: z.string().nullable(),
    completedByEmail: z.string().nullable(),
    completedBalance: z.string().nullable(),
})
    .strict();
export const StartReconciliationRequestSchema = z
    .object({
    bankAccountId: z.uuid(),
    statementDate: IsoDateSchema,
    statementBalance: z
        .string()
        .regex(/^-?\d{1,13}(\.\d{1,2})?$/)
        .optional(),
})
    .strict();
/** A statement balance may legitimately be negative (an overdrawn account). */
const SignedMoneyStringSchema = z
    .string()
    .regex(/^-?\d{1,13}(\.\d{1,2})?$/, 'Amounts are decimal strings with at most 2 decimals');
export const SetStatementRequestSchema = z
    .object({
    statementBalance: SignedMoneyStringSchema,
})
    .strict();
export const SetTicksRequestSchema = z
    .object({
    ticks: z
        .object({ journalLineId: z.uuid(), cleared: z.boolean() })
        .strict()
        .array()
        .min(1),
})
    .strict();
export const AddStatementLineRequestSchema = z
    .object({
    lineDate: IsoDateSchema,
    description: z.string().trim().min(1).max(200),
    reference: z.string().trim().max(60).optional(),
    /** Signed: + money in, − money out. */
    amount: z.string().regex(/^-?\d{1,13}(\.\d{1,2})?$/),
})
    .strict();
export const AdjustmentResultSchema = z
    .object({
    /** BR-FIN-09-09: nothing at all happens when already matched. */
    created: z.boolean(),
    kind: z.enum(['bank_fee', 'interest']).nullable(),
    amount: z.string().nullable(),
    reconciliation: ReconciliationSchema,
})
    .strict();
// ── Cash transfers (FIN-06) ─────────────────────────────────────────────────
export const CashTransferSchema = z
    .object({
    id: z.uuid(),
    /** CT-#### (field 1). */
    code: z.string(),
    transferNo: z.number().int().positive(),
    status: z.enum(['draft', 'posted', 'cancelled']),
    /** GEN-59: why the draft was cancelled — read back off the record. NULL before 20260921100000. */
    cancelReason: z.string().nullable(),
    transferDate: IsoDateSchema,
    reference: z.string().nullable(),
    fromAccountId: z.uuid(),
    fromAccountCode: z.string(),
    fromAccountName: z.string(),
    fromCurrency: z.string(),
    toAccountId: z.uuid(),
    toAccountCode: z.string(),
    toAccountName: z.string(),
    toCurrency: z.string(),
    amount: z.string(),
    exchangeRate: z.string(),
    rateIsManual: z.boolean(),
    /** amount × rate — shown only cross-currency (BR-FIN-06-04). */
    convertedAmount: z.string().nullable(),
    fee: z.string(),
    feeMode: z.enum(['deduct', 'expense']).nullable(),
    feeExpenseAccountId: z.uuid().nullable(),
    /** 5b: source-side total, in the source currency. */
    totalFromSource: z.string(),
    /** 5b: destination-side net, in the destination currency, clamped ≥ 0. */
    netToDestination: z.string(),
    journalEntryId: z.uuid().nullable(),
    postedAt: z.string().nullable(),
    notes: z.string().nullable(),
    createdAt: z.string(),
})
    .strict();
export const CreateCashTransferRequestSchema = z
    .object({
    transferDate: IsoDateSchema,
    reference: z.string().trim().max(200).optional(),
    fromAccountId: z.uuid(),
    toAccountId: z.uuid(),
    amount: MoneyStringSchema.optional(),
    /** Overrides the FIN-05 table's automatic cross rate; flagged manual. */
    exchangeRate: z
        .string()
        .regex(/^\d{1,9}(\.\d{1,6})?$/)
        .optional(),
    fee: MoneyStringSchema.optional(),
    feeMode: z.enum(['deduct', 'expense']).optional(),
    feeExpenseAccountId: z.uuid().optional(),
    notes: z.string().max(2000).optional(),
})
    .strict();
export const UpdateCashTransferRequestSchema = z
    .object({
    transferDate: IsoDateSchema.optional(),
    reference: z.string().trim().max(200).nullable().optional(),
    fromAccountId: z.uuid().optional(),
    toAccountId: z.uuid().optional(),
    amount: MoneyStringSchema.optional(),
    exchangeRate: z
        .string()
        .regex(/^\d{1,9}(\.\d{1,6})?$/)
        .nullable()
        .optional(),
    fee: MoneyStringSchema.optional(),
    feeMode: z.enum(['deduct', 'expense']).nullable().optional(),
    feeExpenseAccountId: z.uuid().nullable().optional(),
    /** The one field that stays editable after posting (BR-FIN-06-13). */
    notes: z.string().max(2000).nullable().optional(),
})
    .strict();
// ── Bank Operations (FIN-12) ────────────────────────────────────────────────
export const BankOverviewSchema = z
    .object({
    baseCurrency: z.string(),
    /** checks.low_balance_alert_threshold — a company setting (FIN-12 defect fixed). */
    lowBalanceThreshold: z.string(),
    /**
     * GEN-115/GEN-116, decided by the SERVER exactly as on DSH-04: the
     * effective date of the rates actually used, or null when NOTHING was
     * converted. The screen shows its disclosure precisely when this is
     * non-null, so the browser never infers from the rows whether a
     * conversion happened. `Consolidation.rateDate` is the value.
     */
    rateDate: IsoDateSchema.nullable(),
    accounts: z
        .object({
        bankAccountId: z.uuid(),
        name: z.string(),
        bankName: z.string().nullable(),
        currency: z.string(),
        accountType: BankAccountTypeSchema,
        balance: z.string().nullable(),
        /** BR-FIN-12-08: the balance ÷ the FIN-05 rate (BR-FIN-05-02's
         *  foreign→base divide). Null while unmapped or no rate exists. */
        baseBalance: z.string().nullable(),
        /** The rate used and the date it applies from — stated beside the
         *  converted figure so the reader knows WHICH rate said so. */
        rate: z.string().nullable(),
        rateDate: IsoDateSchema.nullable(),
        unmapped: z.boolean(),
        reconciliationState: z.enum(['reconciled', 'pending']),
    })
        .strict()
        .array(),
    /** Converted into the base currency (BR-FIN-12-08), reading the FIN-05
     *  table at today's effective rate. Currencies with no rate are NOT
     *  included — each is named in conversionNotes (ERR-DSH-04-04's shape). */
    combinedTotal: z.string(),
    conversionNotes: z.string().array(),
    alerts: z
        .object({
        lowBalance: z
            .object({
            bankAccountId: z.uuid(),
            name: z.string(),
            balance: z.string(),
            currency: z.string(),
            /**
             * FIN-12 §16 — "the low-balance threshold is compared and
             * displayed in the same currency". The comparison was always in
             * base (BR-FIN-12-03); this is the figure it compared, so the
             * card can show the reader the number the decision was made on
             * instead of a native amount beside a base threshold.
             *
             * NON-NULLABLE ON PURPOSE, and this is the reason rather than an
             * oversight: an account is only pushed onto this list from INSIDE
             * `if (baseBalance !== null)` in `bank-operations.service.ts` —
             * an account with no rate is excluded from the comparison
             * entirely and named in `conversionNotes`. Widening this to
             * nullable would admit a row the card cannot render, and would
             * mean an alert had been raised on a balance nothing compared.
             */
            baseBalance: z.string(),
        })
            .strict()
            .array(),
        unreconciledCount: z.number().int(),
        bouncedCount: z.number().int(),
    })
        .strict(),
    chequeTiles: z
        .object({
        direction: CheckDirectionSchema,
        status: CheckStatusSchema,
        count: z.number().int(),
        total: z.string(),
    })
        .strict()
        .array(),
    /**
     * checks.reminder_days drives the window (BR-FIN-12-11 + settings).
     *
     * There is no `reminderMethod` beside it. Delegated ruling D15 (26 Aug
     * 2026) made every notification in-app and retired the settings row
     * `checks.reminder_method` with it, so the screen has no delivery choice
     * left to state — see the Checks group in `org-settings.ts` for the ruling
     * and for what happens to rows tenants already stored.
     */
    reminderDays: z.number().int(),
    upcoming: z
        .object({
        checkId: z.uuid(),
        code: z.string(),
        direction: CheckDirectionSchema,
        party: z.string(),
        chequeDate: IsoDateSchema,
        amount: z.string(),
        currency: z.string(),
        /** cheque date − today: negative = overdue. */
        dueInDays: z.number().int(),
    })
        .strict()
        .array(),
    cashFlow: z
        .object({
        totalIn: z.string(),
        totalOut: z.string(),
        net: z.string(),
    })
        .strict(),
    recentActivity: z
        .object({
        date: IsoDateSchema,
        accountName: z.string(),
        amount: z.string(),
        currency: z.string(),
        memo: z.string().nullable(),
        entryId: z.uuid(),
        /**
         * BR-FIN-12-12 — "every navigation from this screen must select the
         * record it names, not merely open the screen": the DOCUMENT that
         * produced this movement, so the row's reference can link to it and
         * select it (FIN-12 §5g, the defects table's "deep-link them all").
         *
         * NON-NULLABLE on purpose: every posted movement has a journal entry
         * even when no document does — a hand-posted charge falls back to
         * `kind: 'journal'` with the entry's own JE-#### code, so no row is
         * ever a dead end. `direction` is set exactly when kind is 'check'
         * (the register screens are one per direction, FIN-10/FIN-11).
         */
        source: z
            .object({
            kind: z.enum(['receipt', 'payment', 'transfer', 'check', 'journal']),
            id: z.uuid(),
            code: z.string(),
            direction: CheckDirectionSchema.nullable(),
        })
            .strict(),
    })
        .strict()
        .array(),
})
    .strict();
// ── Routers ─────────────────────────────────────────────────────────────────
const checkParams = z.object({ checkId: z.uuid() }).strict();
const reconParams = z.object({ reconciliationId: z.uuid() }).strict();
const transferParams = z.object({ transferId: z.uuid() }).strict();
export const bankingContract = c.router({
    // FIN-09 — accounts.
    listBankAccounts: {
        method: 'GET',
        path: '/api/banking/accounts',
        responses: {
            200: z.object({ accounts: BankAccountSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Bank accounts with derived balances (banking.view).',
    },
    getBankAccount: {
        method: 'GET',
        path: '/api/banking/accounts/:bankAccountId',
        pathParams: z.object({ bankAccountId: z.uuid() }).strict(),
        responses: {
            200: BankAccountSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One bank account (banking.view).',
    },
    createBankAccount: {
        method: 'POST',
        path: '/api/banking/accounts',
        body: CreateBankAccountRequestSchema,
        responses: {
            201: BankAccountSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New bank account (banking.create). Ledger mapping is 1:1 and balance-sheet-shaped (BR-FIN-09-02, Q-FIN-09-5).',
    },
    updateBankAccount: {
        method: 'PATCH',
        path: '/api/banking/accounts/:bankAccountId',
        pathParams: z.object({ bankAccountId: z.uuid() }).strict(),
        body: UpdateBankAccountRequestSchema,
        responses: {
            200: BankAccountSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a bank account (banking.edit) — every field editable (the FIN-09 defect fixed).',
    },
    // FIN-10 / FIN-11 — cheques.
    listChecks: {
        method: 'GET',
        path: '/api/checks',
        query: pagedQuery({
            direction: CheckDirectionSchema.optional(),
            status: CheckStatusSchema.optional(),
            partnerId: z.uuid().optional(),
            /** PV-01's endorsed-cheque picker: the printed number, the issuing bank or the customer's name. */
            q: z.string().trim().max(120).optional(),
            /**
             * BR-FIN-10-15's reminders rail: only cheques still AWAITING ACTION —
             * `in_hand` inbound, `issued` or `given` outbound — soonest first.
             *
             * It exists as a server-side filter because the rail used to be a
             * `.filter()` over the whole register in the browser, and paging the
             * register would have quietly shrunk the rail to whatever happened to
             * be on page 1. A rail that silently forgets an overdue cheque is a
             * worse defect than a slow one.
             */
            awaitingAction: z.enum(['true']).optional(),
        }),
        responses: {
            200: pagedResponse('checks', CheckSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The cheque register, both directions (banking.view), PAGED (NFR-42) — every filter in SQL, including BR-FIN-10-15’s awaiting-action rail.',
    },
    getCheck: {
        method: 'GET',
        path: '/api/checks/:checkId',
        pathParams: checkParams,
        responses: {
            200: CheckSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One cheque (banking.view).',
    },
    createCheck: {
        method: 'POST',
        path: '/api/checks',
        body: CreateCheckRequestSchema,
        responses: {
            201: CheckSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Record a cheque (banking.create): incoming starts In hand, outgoing starts Issued, drawn on a bank/cash account only (BR-FIN-11-01).',
    },
    updateCheck: {
        method: 'PATCH',
        path: '/api/checks/:checkId',
        pathParams: checkParams,
        body: UpdateCheckRequestSchema,
        responses: {
            200: CheckSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a cheque (banking.edit). The AMOUNT is never editable (BR-FIN-10-02).',
    },
    transitionCheck: {
        method: 'POST',
        path: '/api/checks/:checkId/transition',
        pathParams: checkParams,
        body: CheckTransitionRequestSchema,
        responses: {
            200: CheckSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Move the lifecycle (banking.approve — transitions post the §12 ledger legs in the same transaction). Illegal moves are refused; auto-stamps fill only empty dates (BR-FIN-10-05).',
    },
    voidCheck: {
        method: 'POST',
        path: '/api/checks/:checkId/void',
        pathParams: checkParams,
        body: z.object({ reason: z.string().trim().min(1).max(500) }).strict(),
        responses: {
            200: CheckSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Void an outgoing cheque (banking.delete): ONLY from Issued, reason required, number retained (BR-FIN-11-03/-04/-05).',
    },
    reverseCheck: {
        method: 'POST',
        path: '/api/checks/:checkId/reverse',
        pathParams: checkParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: z.object({ check: CheckSchema, message: z.string() }).strict(),
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Reverse a bounced cheque (banking.approve), ONE-SHOT: restores the voucher allocation, posts the reversal + fee, and — incoming only — flags the customer (BR-FIN-10-10/-12, BR-FIN-11-09).',
    },
    reissueCheck: {
        method: 'POST',
        path: '/api/checks/:checkId/reissue',
        pathParams: checkParams,
        body: z.object({}).strict().optional(),
        responses: {
            201: CheckSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Reissue a bounced OUTGOING cheque (banking.create): one real linked replacement (BR-FIN-11-10, the reissue defects fixed).',
    },
    // R10 — the bounced-cheque flag.
    setBouncedFlag: {
        method: 'PATCH',
        path: '/api/checks/bounced-flag/:partnerId',
        pathParams: z.object({ partnerId: z.uuid() }).strict(),
        body: SetBouncedFlagRequestSchema,
        responses: {
            200: z.object({ partnerId: z.uuid(), flagged: z.boolean() }).strict(),
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Set or clear a customer’s bounced-cheque flag by hand (banking.override_bounced_flag — refused however called without it, BR-FIN-10-16). Reason required; audited (BR-FIN-10-17).',
    },
    bouncedFlagHistory: {
        method: 'GET',
        path: '/api/checks/bounced-flag/:partnerId/history',
        pathParams: z.object({ partnerId: z.uuid() }).strict(),
        responses: {
            200: z.object({ history: BouncedFlagHistoryRowSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'The flag’s append-only history, readable wherever the flag is shown (banking.view, BR-FIN-10-17).',
    },
    // FIN-09 — reconciliation.
    listReconciliations: {
        method: 'GET',
        path: '/api/banking/reconciliations',
        query: pagedQuery({
            bankAccountId: z.uuid(),
            /**
             * The screen asks two questions of this history and used to answer
             * both by filtering the whole list in the browser: "is there an open
             * period to continue?" and "show me the completed ones". Paging the
             * list would have made the first answer depend on which page was
             * loaded — so the predicate is a query parameter and runs in SQL.
             */
            status: z.enum(['in_progress', 'completed']).optional(),
        }),
        responses: {
            200: pagedResponse('reconciliations', ReconciliationHistoryRowSchema),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Reconciliation history for an account, newest first (banking.view, BR-FIN-09-13), PAGED (NFR-42) — one statement a month for as long as the account has existed.',
    },
    startReconciliation: {
        method: 'POST',
        path: '/api/banking/reconciliations',
        body: StartReconciliationRequestSchema,
        responses: {
            201: ReconciliationSchema,
            200: ReconciliationSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Start (or return the existing) period for an account + statement date (banking.edit). Refused while the account is unmapped (FIN-09 defects).',
    },
    getReconciliation: {
        method: 'GET',
        path: '/api/banking/reconciliations/:reconciliationId',
        pathParams: reconParams,
        responses: {
            200: ReconciliationSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'The workbench: book lines (ledger postings on the account) + ticks + adjustments + the §5d figures (banking.view).',
    },
    setStatement: {
        method: 'PATCH',
        path: '/api/banking/reconciliations/:reconciliationId/statement',
        pathParams: reconParams,
        body: SetStatementRequestSchema,
        responses: {
            200: ReconciliationSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Edit the statement ending balance (banking.edit). On a completed period this REOPENS it, audited (BR-FIN-09-08).',
    },
    setTicks: {
        method: 'PUT',
        path: '/api/banking/reconciliations/:reconciliationId/ticks',
        pathParams: reconParams,
        body: SetTicksRequestSchema,
        responses: {
            200: ReconciliationSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Tick or untick book movements (banking.edit). INERT once completed — 409 (BR-FIN-09-07).',
    },
    addStatementLine: {
        method: 'POST',
        path: '/api/banking/reconciliations/:reconciliationId/statement-lines',
        pathParams: reconParams,
        body: AddStatementLineRequestSchema,
        responses: {
            200: ReconciliationSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Type/import a statement line awaiting a match (banking.edit).',
    },
    addAdjustment: {
        method: 'POST',
        path: '/api/banking/reconciliations/:reconciliationId/adjustment',
        pathParams: reconParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: AdjustmentResultSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Add adjustment (banking.approve — posts a journal entry): ONE pre-ticked movement for exactly the outstanding difference, bank fee or interest by direction; nothing at all when already matched (BR-FIN-09-09, FIN-09 §12).',
    },
    completeReconciliation: {
        method: 'POST',
        path: '/api/banking/reconciliations/:reconciliationId/complete',
        pathParams: reconParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: ReconciliationSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Mark as reconciled (banking.approve): refused while the difference is not zero (BR-FIN-09-06); locks the period and appends the history row (BR-FIN-09-13).',
    },
    reopenReconciliation: {
        method: 'POST',
        path: '/api/banking/reconciliations/:reconciliationId/reopen',
        pathParams: reconParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: ReconciliationSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Explicitly reopen a completed period (banking.approve — the same permission that made the assertion withdraws it, FIN-09 §9). Audited.',
    },
    // FIN-06 — cash transfers.
    listCashTransfers: {
        method: 'GET',
        path: '/api/banking/transfers',
        query: pagedQuery({ status: z.enum(['draft', 'posted', 'cancelled']).optional() }),
        responses: {
            200: pagedResponse('transfers', CashTransferSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Cash transfers (banking.view), PAGED (NFR-42) — the status filter in SQL.',
    },
    getCashTransfer: {
        method: 'GET',
        path: '/api/banking/transfers/:transferId',
        pathParams: transferParams,
        responses: {
            200: CashTransferSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One transfer (banking.view).',
    },
    createCashTransfer: {
        method: 'POST',
        path: '/api/banking/transfers',
        body: CreateCashTransferRequestSchema,
        responses: {
            201: CashTransferSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New draft transfer (banking.create) between two DIFFERENT cash-family ledger accounts (BR-FIN-06-01).',
    },
    updateCashTransfer: {
        method: 'PATCH',
        path: '/api/banking/transfers/:transferId',
        pathParams: transferParams,
        body: UpdateCashTransferRequestSchema,
        responses: {
            200: CashTransferSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a DRAFT (banking.edit); after posting only the notes accept typing (BR-FIN-06-13).',
    },
    removeCashTransfer: {
        method: 'DELETE',
        path: '/api/banking/transfers/:transferId',
        pathParams: transferParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete a DRAFT (banking.delete) — R8/BR-FIN-06-16.',
    },
    cancelCashTransfer: {
        method: 'POST',
        path: '/api/banking/transfers/:transferId/cancel',
        pathParams: transferParams,
        /** GEN-59: never empty — the reason is the act's price of admission. */
        body: z
            .object({
            reason: z.string().trim().min(1, BANKING_MESSAGES.cancelReasonRequired).max(500),
        })
            .strict(),
        responses: {
            200: CashTransferSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Draft → Cancelled (banking.edit); no route out of Posted (R8). GEN-59: reason required, stored on the transfer and written to the audit row.',
    },
    postCashTransfer: {
        method: 'POST',
        path: '/api/banking/transfers/:transferId/post',
        pathParams: transferParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: CashTransferSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Post transfer (banking.approve): writes the FIN-06 §12 entry — both fee modes — and locks every figure.',
    },
    // FIN-12 — the morning page.
    overview: {
        method: 'GET',
        path: '/api/banking/overview',
        query: z
            .object({
            from: IsoDateSchema.optional(),
            to: IsoDateSchema.optional(),
        })
            .strict(),
        responses: {
            200: BankOverviewSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Bank Operations (banking.view): balances + combined total, alerts, cheque tiles, the settings-driven upcoming list, cash flow and recent activity — POSTED movements only (R9). The date range governs only cash flow and activity (BR-FIN-12-02).',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=banking.js.map